import { carteLieeDuSac, totalDuSac } from '../../lib/liens.js';
import type { CarteFichiers } from '../screens.js';
import type { LinkedCardDef, RollupFieldDef } from '../contract.js';

/* ------------------------------------------------------------------ */
/* Les cartes declarees d'une fiche, lues dans le sac du binding       */
/* ------------------------------------------------------------------ */

/*
 * SEULES DE LEUR MODULE, sans rien de rendu : le barrel de `@taki/ui` les
 * exporte (les vues de fiche du runtime lisent les cartes comme la Fiche) sans
 * embarquer la Fiche, qui vit dans le sous-chemin `@taki/ui/views/core`.
 */

/**
 * UNE ENTREE DE CARTE : un nom de champ nu, ou un TOTAL sur un jeu lie.
 *
 * LES DEUX VIVENT DANS LA MEME LISTE, a leur rang : une carte garde UN ordre,
 * et deux listes a recoller (les champs d'un cote, les totaux de l'autre)
 * auraient rendu impossible de poser « Heures » entre deux champs.
 */
export type RecordCardEntry = string | { rollup: RollupFieldDef };

/**
 * LA DISPOSITION D'UNE CARTE DE CHAMPS : comment elle occupe sa rangee, jamais
 * OU elle se pose (le plan fait couler les cartes dans l'ordre de declaration).
 *
 * ELLE EST RESOLUE, JAMAIS ABSENTE, des qu'on est ici : le sac du binding, lui,
 * n'ecrit pas le defaut (`half` ne s'y trouve jamais). Le laisser facultatif a
 * l'interieur ferait relire l'absence a chaque endroit qui la consulte, et un
 * oubli rendrait une carte dans la mauvaise colonne sans rien faire rougir.
 */
export type CardLayout = 'half' | 'full' | 'title-full';

/** La lecture du sac : tout ce qui n'est pas une disposition connue vaut `half`. */
function layoutDuSac(valeur: unknown): CardLayout {
  return valeur === 'full' || valeur === 'title-full' ? valeur : 'half';
}

/**
 * Une CARTE declaree par l'ecran, dans le sac libre du binding
 * (`ViewBinding.options.cards`). DEUX NATURES, et la carte DIT laquelle
 * (`kind`) plutot que de la faire deviner :
 *
 * - `fields` : des champs de la fiche. `keys` melange librement cles de
 *   colonnes, noms de roles et totaux ; `layout` dit COMMENT la carte occupe sa
 *   rangee, jamais ou elle se pose (voir `FieldCard` pour les trois valeurs) ;
 * - `linked` : les fiches d'une AUTRE porte, rendues par une vue du socle
 *   (voir `LinkedCard`). Toujours pleine largeur.
 * - `files` : les fichiers joints, remplis par l'hote. Toujours pleine largeur.
 *
 * UNE CARTE SANS `kind` EST UNE CARTE DE CHAMPS : c'est ce que les ecrans
 * d'avant ce chantier declarent, et ils n'ont rien a changer.
 */
export type RecordCardDef =
  | {
      kind: 'fields';
      title: string;
      keys: RecordCardEntry[];
      /**
       * LES CLEFS QUE CETTE CARTE GARDE EN LECTURE, en clefs NUES.
       *
       * UNE CASE EST MODIFIABLE PAR DEFAUT des que l'ecran a fourni de quoi
       * ecrire son champ : la fiche est le seul endroit ou l'on corrige une
       * valeur, donc le refus doit etre ECRIT, pas subi. Ce verrou est ce
       * refus, et il ne touche QUE la fiche : la meme colonne reste modifiable
       * au tableau.
       */
      locked: Set<string>;
      layout: CardLayout;
      /** La carte va dans la COLONNE DE DROITE (voir `planDesCartes`). Absent : le flot. */
      right?: true;
    }
  | { kind: 'linked'; card: LinkedCardDef }
  | { kind: 'files'; carte: CarteFichiers };

/**
 * La declaration de cartes relue depuis le sac libre, DEFENSIVEMENT : le sac
 * est un `Record<string, unknown>`, rien n'y est garanti. Une entree invalide
 * est ignoree plutot que de casser la fiche ; aucune entree valide = aucune
 * declaration, et la fiche retombe sur sa carte unique.
 *
 * C'est la seule traduction de la fiche qui puisse se tromper en silence (une
 * carte qui disparait ne fait rougir aucun build), et elle est PURE : la Fiche
 * (`RecordView`) et les vues de fiche du runtime (`lectureDeLaFiche`) la lisent
 * toutes deux, une seule lecture des cartes.
 */
export function cardDefsOf(options: Record<string, unknown>): RecordCardDef[] {
  const raw = options['cards'];
  if (!Array.isArray(raw)) return [];
  const defs: RecordCardDef[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry == null) continue;
    const candidate = entry as {
      kind?: unknown;
      title?: unknown;
      keys?: unknown;
      locked?: unknown;
      layout?: unknown;
      side?: unknown;
    };
    if (candidate.kind === 'linked') {
      // La carte liee part TELLE QUELLE (identite comprise, voir
      // `carteLieeDuSac`) : la fiche ne sait pas la lire, elle la monte.
      const card = carteLieeDuSac(entry);
      if (card != null) defs.push({ kind: 'linked', card });
      continue;
    }
    if (candidate.kind === 'files') {
      /* LA CARTE DES FICHIERS part elle aussi telle quelle : seul son titre est
         exige, c'est lui que la fiche et l'hote affichent. Une seule par
         fiche, la premiere gagne (le schema en refuse deux, ceci est le filet). */
      const carte = (entry as { carte?: unknown }).carte;
      if (typeof carte !== 'object' || carte == null) continue;
      const { titre, modifiable, id, vignettes } = carte as {
        titre?: unknown;
        modifiable?: unknown;
        id?: unknown;
        vignettes?: unknown;
      };
      if (typeof titre !== 'string' || titre.trim() === '') continue;
      if (defs.some((def) => def.kind === 'files')) continue;
      defs.push({
        kind: 'files',
        carte: {
          titre,
          fichiers: true,
          ...(typeof id === 'string' ? { id } : {}),
          ...(modifiable === false ? { modifiable: false as const } : {}),
          ...(vignettes === 'petites' || vignettes === 'moyennes' || vignettes === 'grandes' ? { vignettes } : {}),
        },
      });
      continue;
    }
    if (typeof candidate.title !== 'string' || candidate.title.trim() === '') continue;
    if (!Array.isArray(candidate.keys)) continue;
    const keys = candidate.keys.flatMap((key): RecordCardEntry[] => {
      if (typeof key === 'string') return key === '' ? [] : [key];
      const rollup = totalDuSac(key);
      return rollup == null ? [] : [{ rollup }];
    });
    if (keys.length === 0) continue;
    // Le verrou est FACULTATIF : une carte qui n'en porte pas ouvre toutes ses
    // cases que l'ecran sait ecrire. Un sac malforme ne verrouille rien plutot
    // que de figer une fiche entiere.
    const locked = new Set(
      (Array.isArray(candidate.locked) ? candidate.locked : []).filter(
        (key): key is string => typeof key === 'string' && key !== '',
      ),
    );
    defs.push({
      kind: 'fields',
      title: candidate.title,
      keys,
      locked,
      layout: layoutDuSac(candidate.layout),
      /* LA DROITE N'EST HONOREE QU'EN DEMI : le schema refuse deja l'autre
         cas, ceci est le filet d'un sac ecrit a la main. */
      ...(candidate.side === 'right' && layoutDuSac(candidate.layout) === 'half'
        ? { right: true as const }
        : {}),
    });
  }
  return defs;
}
